import { useMutation, useQueryClient } from '@tanstack/react-query';

import { authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';

interface RenameAnalysisInput {
  projectId: string;
  analysisProfileId: string;
  name: string;
}

/**
 * Rename an analysis from the artifacts page. PATCHes the profile name; only the
 * creator may rename (a non-creator gets a 404, surfaced to the caller). Analyses
 * in a project may share a name, so there is no duplicate-name error.
 */
export const useRenameAnalysisMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, RenameAnalysisInput>({
    mutationFn: ({ projectId, analysisProfileId, name }) =>
      authFetch<void>(
        `/analyses/${encodeURIComponent(projectId)}` +
          `/analysis-profile/${encodeURIComponent(analysisProfileId)}/rename`,
        {
          method: 'PATCH',
          body: JSON.stringify({ name }),
        }
      ),
    onSuccess: (_data, { projectId, analysisProfileId }) => {
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.artifactsForProject(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.activityForProject(projectId),
      });
      // Same tuple as `analysisKeys.profile` in feature-analysis (not importable
      // here), so an already-open analysis page doesn't keep the old name.
      queryClient.invalidateQueries({ queryKey: ['analysis', 'profile', analysisProfileId] });
    },
  });
};
